import React from "react";

export const PRINT_CSS = `
  @page { size: A4 portrait; margin: 12mm; }

  * { box-sizing: border-box; }

  html, body { margin: 0; padding: 0; background: #fff; }

  body {
    color: #000;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 11px;
    line-height: 1.35;
  }

  .ps-title { font-size: 18px; margin: 0 0 2px; }
  .ps-printed { font-size: 10px; color: #555; margin-bottom: 8px; }

  h2 {
    font-size: 13px;
    margin: 14px 0 4px;
    padding-bottom: 2px;
    border-bottom: 1.5px solid #000;
    break-after: avoid;
  }

  /* ---------- base tables ---------- */
  table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
  }

  th, td {
    border: 1px solid #999;
    padding: 3px 5px;
    text-align: left;
    vertical-align: top;
    word-wrap: break-word;
    overflow-wrap: break-word;
    word-break: break-word;
  }

  th { background: #eee; font-weight: 700; }

  /* ---------- episode header table ---------- */
  .ps-episode th,
  .ps-episode td {
    text-align: center;
    font-size: 10px;
  }

  /* ---------- DRG information (ps-kv) ---------- */
  .ps-kv { table-layout: fixed; }
  .ps-kv th {
    width: 12%;
    white-space: normal;
    text-align: left;
    vertical-align: top;
  }
  .ps-kv td {
    width: 13%;
    vertical-align: top;
  }
  .ps-kv td[colspan] { width: auto; }

  /* ---------- code tables (ps-codes) ---------- */
  .ps-codes thead { display: table-header-group; }
  .ps-codes tr {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .ps-codes .c-num {
    width: 30px;
    text-align: center;
    white-space: nowrap;
  }
  .ps-codes .c-small {
    width: 60px;
    text-align: center;
    white-space: nowrap;
  }
  .ps-codes .c-code {
    width: 95px;
    white-space: nowrap;
    font-weight: 600;
  }
  .ps-codes .c-desc {
    white-space: normal;
    word-break: break-word;
  }

  /* ---------- misc ---------- */
  .ps-empty { font-style: italic; color: #555; }

  @media print {
    html, body {
      width: 186mm;
      max-width: 186mm;
      overflow: hidden;
    }
    table { width: 100%; }
    .ps-codes thead { display: table-header-group; }
    .ps-codes tr {
      break-inside: avoid;
      page-break-inside: avoid;
    }
  }
`;

const splitCode = (rawCode = "", type) => {
  const code = String(rawCode || "").trim();
  if (type === "procedure") return { cluster: "", cof: "", base: code };
  if (["1", "2"].includes(code[1])) {
    return { cluster: code[0], cof: code[1], base: code.slice(2) };
  }
  return { cluster: "", cof: code[0] || "", base: code.slice(1) };
};

const val = (v) => (v === null || v === undefined || v === "" ? "-" : v);

const PrintSummary = ({
  codingscreendata,
  diagnosisStates,
  isOutPatient,
  drgData,
  selectedVersion,
  isUSA,
}) => {
  const meta = codingscreendata?.data?.meta || {};
  const statusList = Array.isArray(meta.status) ? meta.status : [];
  const primary = statusList.find((s) => s?.is_primary) || statusList[0];

  const episodeInfo = [
    ["MRN", meta.medical_record_number],
    ["Episode ID", meta.episode_identifier],
    ["Admission Date", meta.admission_date],
    ["Discharge Date", meta.separation_date],
    ["Length of Stay", meta.los ?? 0],
    ["Age", meta.age_y],
    ["Sex", meta.sex],
    ["Speciality", meta.speciality],
    ["Status", primary?.status || "-"],
  ];

  const selectedDrg = drgData?.drgs?.find(
    (o) => o[selectedVersion] !== undefined,
  )?.[selectedVersion];
  const info = selectedDrg?.additional_information || {};
  const weightOk =
    selectedDrg?.weight && selectedDrg.weight !== "can't determine";
  const reimbursement = !weightOk
    ? "-"
    : isUSA
      ? `$${Math.round(selectedDrg.reimbursement).toLocaleString()}`
      : `$${Math.round(selectedDrg.weight * 7258).toLocaleString()}`;
  const versionLabel = !selectedVersion
    ? "-"
    : isUSA
      ? "MS-DRG Version 43"
      : `AR-DRG V ${selectedVersion}`;

  const valid = (arr) => (arr || []).filter((i) => i?.code);
  const sections = [
    ...(isOutPatient
      ? []
      : [
          {
            key: "principal",
            title: "Principal Diagnosis",
            items: valid(diagnosisStates?.principal),
          },
          {
            key: "additional",
            title: "Additional Diagnoses",
            items: valid(diagnosisStates?.additional),
          },
        ]),
    {
      key: "procedure",
      title: "Procedure Codes",
      items: valid(diagnosisStates?.procedure),
    },
  ];
  let counter = 0;

  // Manual DD/MM/YYYY HH:mm:ss — locale-independent
  const formatPrintedDate = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(
      d.getHours(),
    )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  return (
    <div>
      <h1 className="ps-title">Coding Summary</h1>
      <div className="ps-printed">
        Printed: {formatPrintedDate(new Date())}
      </div>

      <table className="ps-episode">
        <tbody>
          <tr>
            {episodeInfo.map(([l]) => (
              <th key={l}>{l}</th>
            ))}
          </tr>
          <tr>
            {episodeInfo.map(([l, v]) => (
              <td key={l}>{val(v)}</td>
            ))}
          </tr>
        </tbody>
      </table>

      {!isOutPatient && (
        <div>
          <h2>DRG Information</h2>
          <table className="ps-kv">
            <tbody>
              <tr>
                <th>DRG Version</th>
                <td>{versionLabel}</td>
                <th>DRG</th>
                <td>{val(selectedDrg?.drg)}</td>
                <th>Weight</th>
                <td>
                  {weightOk ? Number(selectedDrg.weight).toFixed(4) : "-"}
                </td>
                <th>Reimbursement</th>
                <td>{reimbursement}</td>
              </tr>
              <tr>
                <th>DRG Title</th>
                <td colSpan={7}>{val(info.description)}</td>
              </tr>
              <tr>
                <th>Inlier Wgt</th>
                <td>{val(info.inlier)}</td>
                <th>Lower Bound</th>
                <td>{val(info.lower_bound)}</td>
                <th>Upper Bound</th>
                <td>{val(info.upper_bound)}</td>
                <th>Same Day</th>
                <td>{val(info.same_day)}</td>
              </tr>
              <tr>
                <th>ALOS</th>
                <td>{val(info.alos)}</td>
                {!isUSA && (
                  <>
                    <th>ECCS</th>
                    <td>
                      {info.eccs !== undefined &&
                      info.eccs !== null &&
                      !isNaN(Number(info.eccs))
                        ? Number(info.eccs).toFixed(1)
                        : "-"}
                    </td>
                    <th>MDC</th>
                    <td colSpan={3}>
                      {info.mdc_code
                        ? `${info.mdc_code} - ${info.mdc_description || ""}`
                        : "-"}
                    </td>
                  </>
                )}
              </tr>
              {!isUSA && info.eccs_criteria?.length > 0 && (
                <tr>
                  <th>ECCS Criteria</th>
                  <td colSpan={7}>
                    {info.eccs_criteria
                      .map(
                        (c) =>
                          `${c?.complexity || "-"}: ${
                            c?.criteria?.trim() || "ECCS -"
                          }`,
                      )
                      .join("   |   ")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {sections.map((sec) => (
        <div key={sec.key}>
          <h2>{sec.title}</h2>
          {sec.items.length === 0 ? (
            <div className="ps-empty">No codes</div>
          ) : (
            <table className="ps-codes">
              <thead>
                <tr>
                  <th className="c-num">#</th>
                  {sec.key !== "procedure" && (
                    <th className="c-small">Cluster</th>
                  )}
                  {sec.key !== "procedure" && <th className="c-small">COF</th>}
                  <th className="c-code">Code</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {sec.items.map((item, i) => {
                  counter += 1;
                  const { cluster, cof, base } = splitCode(item.code, sec.key);
                  return (
                    <tr key={`${sec.key}-${item.code}-${i}`}>
                      <td className="c-num">{counter}</td>
                      {sec.key !== "procedure" && (
                        <td className="c-small">{cluster}</td>
                      )}
                      {sec.key !== "procedure" && (
                        <td className="c-small">{cof}</td>
                      )}
                      <td className="c-code">{base}</td>
                      <td className="c-desc">{item.description}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </div>
  );
};

export default PrintSummary;